import { Inject, Injectable } from '@angular/core';
import {
  ApiResourceLocation,
  ResourceService,
  SOFTLINE_SERVICE_HTTP,
} from '@softline/core';
import { Observable } from 'rxjs';
import { Absatzplan, PatchAbsatzplan } from '../types/absatzplan';
import {
  SOFTLINE_API_ABSATZPLAN_COPY,
  SOFTLINE_API_ABSATZPLAN_ENTITY,
  SOFTLINE_API_ABSATZPLAN_KOPF_TEMP_LOESCHEN,
  SOFTLINE_API_ABSATZPLAN_KOPF_VORHANDEN,
  SOFTLINE_API_ABSATZPLAN_QUERY,
  SOFTLINE_API_ABSATZPLAN_REZEPTUR_AUFLOESEN,
  SOFTLINE_API_ABSATZPLAN_SAVE,
} from '../absatzplan.api';
import { AbsatzplanStatus } from '../types/absatzplan-status';
import { KopfVorhandenResult } from '../types/kopf-vorhanden.result';

@Injectable()
export class AbsatzplanService {
  constructor(
    @Inject(SOFTLINE_SERVICE_HTTP)
    private readonly service: ResourceService<ApiResourceLocation>
  ) {}

  load(params: {
    queryParams: Record<string, unknown>;
    formData: Record<string, unknown>;
  }): Observable<Absatzplan> {
    return this.service.create(
      { path: SOFTLINE_API_ABSATZPLAN_QUERY, queryParams: params.queryParams },
      params.formData
    );
  }

  patch(
    id: number,
    params: { queryParams: Record<string, unknown>; body: PatchAbsatzplan }
  ): Observable<Absatzplan> {
    return this.service.patch(
      {
        path: SOFTLINE_API_ABSATZPLAN_ENTITY + `/${id}`,
        queryParams: params.queryParams,
      },
      params.body
    );
  }

  freigeben(
    id: number,
    params: { queryParams: Record<string, unknown>; status: AbsatzplanStatus }
  ): Observable<{ status: AbsatzplanStatus }> {
    return this.service.patch(
      {
        path: SOFTLINE_API_ABSATZPLAN_ENTITY + `/${id}`,
        queryParams: params.queryParams,
      },
      { status: params.status }
    );
  }

  updateKommentar(
    id: number,
    params: { queryParams: Record<string, unknown>; kommentar: string | null }
  ): Observable<{ id: number; kommentar: string }> {
    return this.service.patch(
      {
        path: SOFTLINE_API_ABSATZPLAN_ENTITY + `/${id}`,
        queryParams: params.queryParams,
      },
      { kommentar: params.kommentar }
    );
  }

  speichern(body: {
    id: number;
    planvariante: string[];
  }): Observable<Absatzplan> {
    return this.service.create({ path: SOFTLINE_API_ABSATZPLAN_SAVE }, body);
  }

  createEntry(body: {
    idktostamm: number;
    idartstamm: number;
    vegp: number;
    kggp: number;
    monate: string[];
    actDate: string;
  }): Observable<Absatzplan> {
    return this.service.create({ path: SOFTLINE_API_ABSATZPLAN_ENTITY }, body);
  }

  kopieren(params: {
    idSource: number;
    targetName: string;
    datum: string;
  }): Observable<Absatzplan> {
    return this.service.create({ path: SOFTLINE_API_ABSATZPLAN_COPY }, params);
  }

  loeschen(id: number): Observable<Absatzplan> {
    return this.service.delete({
      path: SOFTLINE_API_ABSATZPLAN_ENTITY + `/${id}`,
    });
  }

  kopfVorhanden(): Observable<KopfVorhandenResult> {
    return this.service.get<KopfVorhandenResult>({
      path: SOFTLINE_API_ABSATZPLAN_KOPF_VORHANDEN,
    });
  }

  vorhandenenKopfLoeschen(): Observable<boolean> {
    return this.service.delete({
      path: SOFTLINE_API_ABSATZPLAN_KOPF_TEMP_LOESCHEN,
    });
  }

  vorhandenenKopfEinlesen(
    queryParams: Record<string, unknown>
  ): Observable<Absatzplan> {
    return this.service.create(
      { path: SOFTLINE_API_ABSATZPLAN_QUERY, queryParams },
      {}
    );
  }

  rezepturAufloesen(id): Observable<boolean> {
    return this.service.create(
      { path: SOFTLINE_API_ABSATZPLAN_REZEPTUR_AUFLOESEN, pathParams: { id } },
      {}
    );
  }
}
